// We have these default feature settings, and we're listening to them
// from Firestore.  We also put them into `window` in the `play` function,
// and we save them in local storage.  The reason we don't completely
// rely on the `play` function is that iOS will cache the results of `play`
// until we redeploy, so we couldn't change the feature settings unless
// we deploy again.

import { experimentDefaultValues } from "./experiments";

// NOTE: Don't change the type of any of these, or our local storage saving will break.
// You can add or remove, but do not change any type.
export const defaultFeatureSettings = {
    ...experimentDefaultValues,

    // API
    apiV2RoutesOnly: false,
    allowTableDeletion: false,
    createTableFromStashWithTemporal: false,
    allowDefaultContextUserProfileRow: false,

    // SQL
    linkSQLFileTrackingTableToApps: false,
    sqlRediscoverOnReload: false,
    sqlContinuations: false,
    sqlHideSystemTables: false,
    sqlShowDetailedErrorMessageInPlayer: false,
    sqlEnforceRowLimit: false,
    sqlLinkSpecialTablesWhenAddingPlugin: false,

    // GBT
    gbtFileTrackingInTransactions: false,
    gbtUntrackSoftDeletedRows: false,
    gbtFileTrackingCronJob: false,
    gbtRepackCronJob: false,
    // NOTE: This is on by default so that tests run with it on
    gbtSoftDeleteRows: true,
    gbtRowDeletionCronJob: false,
    gbtShortenHugeTableRetention: false,
    // NOTE: This is on by default so that tests run with it on
    gbtContinuationsWithAppUserID: true,
    // If this is set then we will first try to query the async replica GBT
    // database, and only if it doesn't have the minimum version we need will
    // we fall back to the primary.
    gbtQueryReplica: true,
    bigTablesMaintenanceCronEnabled: false,
    // This will make it so queries to Big Tables only return the first 10
    // rows if the eminence is not set.  We'll turn this on after the beta.
    enforceBigTablesEminence: false,
    gbtCreateIndexesFromRequests: false,
    // NOTE: This is on by default so that tests run with it on
    gbtAsNumberV2: true,
    // NOTE: This is on by default so that tests run with it on
    gbtCanonicalArrayOverlap: true,
    gbtIndexRequestDurationThreshold: false,
    gbtNewTableInSmallestShard: false,
    gbtCacheSmallestShard: false,
    gbtLiveUpdates: false,
    // NOTE: This is on by default so that tests run with it on
    gbtRetryFailedReplicaQueries: true,
    // Fix for CSV imports not restoring previously deleted rows in Big Tables
    fixGbtCsvReimport: false,
    // Fix for calendar drag-and-drop bugs: off-by-one day when using same column, and duration loss when using different columns
    fixGbtAllDayCalendarMove: false,
    gbtFileTrackingNonDirectConnection: false,
    glideTablesOnGBT: true,
    gbtUserProfileTables: false, // Only controls Big Tables, NOT Glide Tables in GBT data store
    gbtMaintainUserProfiles: true, // This one controls for both Big Tables and Glide Tables in GBT data store
    newTeamHasNewGlideTablesProbability: 0, // Controls the probability that a new team will have their `newGlideTables` user feature set
    showNewGlideTablesInExperiments: false, // Controls whether the "newGlideTables" experiment can be enabled by users
    newGlideTablesKillSwitch: false, // When `true`, old Glide Tables will be created for all users, regardless of the setting of their `newGlideTables` user feature
    gbtUseTableStoreProbability: 0,
    tableStoreExperiments: false,
    allowGBTDeepLookups: false,
    gbtAlsoUpdateInLoadingTables: false,
    // NOTE: Set to 0 to disable.
    gbtMaxArrayOverlapSize: 1000,
    gbtClientMaxRetries: 3,
    // Milliseconds a workflow run-start log write waits for the run-log table's
    // `table_directory` lock before aborting and retrying, instead of hanging
    // for the DB `statement_timeout` and returning a hard 500. Set to 0 to
    // disable (fall back to the previous behavior).
    gbtRunLogWriteLockTimeoutMs: 2500,
    gbtFixRefreshDeletes: false,
    gbtFixBatchUserTablePromotion: false,
    gbtTreat403AsEmpty: false,
    // When on, the query freshener retries queries that finished in an error
    // state (bounded by a per-query attempt cap, and skipping clearly-permanent
    // errors), so a transient failure recovers without a full page reload.
    gbtRetryErroredQueries: false,
    gbtFixUnsupportedUnicodeOnImport: false,
    gbtCacheAccessibleBigTables: false,
    gbtSupportUserSpecificWithoutUser: false,
    gbtDontQuerySmallTableUserProfiles: false,
    enableCronReindexFileTrackingForBigTables: false,
    gbtDontRetrySmallTables404: false,
    blockFirestoreListenToSmallTables: false,
    gbtClearSmallTablesOnAppUserChange: false,

    // Derived values cache
    cacheDerivedValues: false,

    // GCP identity tokens: cache them per audience instead of minting one
    // per request, which overran the project-wide `GenerateIdToken` quota.
    cacheIdentityTokens: false,

    // Automations
    automationsPersistUnconfirmedMutations: false,
    automationsSpeedUpIdempotentSteps: false,
    automationsComputationModelTimeoutPermanent: false,
    automationsPassPostActionFailuresThrough: false,
    automationsFailEnqueueDataActionImmediately: false,
    allowTriggerAutomationAction: false,
    automationsShortCircuitRowIDQueries: false,
    automationsLoopIndex: false,

    // PGmirror
    pgMirrorCountRowsChangedForBilling: false,
    pgMirrorFullyPreliminaryRows: false,
    checkpointPgMirrorReloads: false,
    unlimitedRowsPGMirror: false,
    pgMirrorCheckLastReloadedAt: false, // if this is enabled
    pgMirrorCheckLastReloadedAtSampleProbability: false, // then we check with this probability
    pgMirrorCheckLastAirtableBackstop100Hours: false, // probability * 100 hours, so 0.5 is 50 hours
    pgMirrorAvoidReloadIfNoChanges: false,

    // Data import
    useBetterDateInferenceOnImport: false,
    restoreDeletedRowsOnNativeTableImport: false,
    useBetterDateOnlyInferenceOnImport: false,
    importDateOnlyTimeZoneAgnostic: false,
    fixGoogleSheetImport: false,
    moreImportObservability: false,
    fixXLSXErrorCellImport: false,

    // Alpha Waitlist Survey
    alphaWaitlistSurveyEnabled: false,

    // AI Agent
    allowAIAgent: false,
    allowAIAgentOnboarding: false,
    concurrentEditing: false,
    useAppMutationManagerForReadOnly: false,
    fixRefreshBuilderLock: false,
    fixSetLastSessionID: false,
    unlockBuilderAppViaBeacon: false,
    checkPatchAppConsistency: false,
    apiAllowRowIDs: false,
    listenToPublishState: false,
    useAgentBillingEnforcement: false,
    snapshotToken: false,
    backupAppDescriptionSynchronously: false,
    obeyAIAgentCohort: false,

    // Multiplayer
    builderActionVersions: false,
    saveBuilderActionsViaMutationManager: false,
    useBuilderActionsFromMutationManager: false,
    undoViaMutationManager: false,
    allowMultiplayer: false,
    cacheBuilderActionVersions: false,

    // Folder permission
    folderPermissions: false,
    disableMyApps: false,
    transferAppToOrgViaAdminMiddleware: false,

    // File upload
    proxyUploadsViaSameOrigin: false,
    fileTrackingV2TrackUploads: false,
    disableFrontendEncodeUploadURI: false,
    uploadHandlerLoopPrevention: false,
    verifyGCSFileOnUpload: false,
    uploadAppFileV2New: false,

    // File tracking
    fileTrackingReadOnlyDB: false,
    fileTrackingV2AutomaticDeletion: false,
    // on by default so that tests run with it on
    fileTrackingFixGCSEdgeCases: true,

    // Plugins
    // We want to have some features as opt in until we release pricing v4
    "pricingv4-allowTriggerWebhookPlugin": false,
    "pricingv4-allowOpenAIv2Plugin": false,
    allowCustomAIComponent: false,
    allowAIComponentSharing: false,
    allowPluginEmailTrigger: false,
    allowPluginPushNotifications: false,
    allowGlideAPIPlugin: false,
    allowPluginSlackTrigger: false,
    pluginActionWriteback: false,
    "plugin-push-notifications": false,
    pluginLearnMoreLinkToActions: false,
    proV2OnlyHasFreeTierPlugins: false,
    forceRedirectThroughDeployedInPluginOAuth: true,
    enableBigQueryPlugin: false,
    validatePluginWithOAuthCheckFirst: false,
    passJSONAsJSONToPlugins: false,
    rehostFileTimeout: false,
    allowPluginMembraneQuickbooks: false,
    allowPluginMembraneSalesforce: false,
    allowPluginMembraneXero: false,
    allowPluginCodeSandbox: false,
    pluginBooleansFromColumns: false,
    // Generate CSV requeries its queryable input table (a Big Table or a
    // Glide Table in the GBT data store) when the action runs, instead of
    // using the rows already loaded in the session, which can be stale in
    // long-lived sessions:
    // https://github.com/glideapps/glide/issues/38283
    generateCSVRequeriesInputTable: false,
    pluginPaywallForUnconfiguredComputations: false,
    // ##unifiedPluginEminenceChecks:
    // If this is enabled, we'll only have a single implementation of checking
    // whether a plugin is allowed given the eminence flags.
    unifiedPluginEminenceChecks: false,
    // ##pluginAllowListTakesPrecedence:
    // If this is enabled, pluginAllowList will take precedence over pluginDenyList.
    // When disabled (default), pluginDenyList takes precedence (current behavior).
    pluginAllowListTakesPrecedence: false,
    pluginsOnlyPublishUsedMetadata: false,
    betterSSRFDetection: false,

    enableBillingLockoutOnFailedPayments: false,
    enableBillingUnlockOnRecoveredSubscriptions: false,
    enableBillingOrganizationLockoutEvents: false,
    enableUsageBlockingOnLockout: false,

    allowNewAppModalWorkflow: false,
    allowRenderableContentComponent: false,
    allowIFrameEmbedding: false,
    allowPluginSnowflake: false,
    allowHumanInTheLoop: false,
    showMissingEntities: false,
    flyio: false,
    templateSubmit: false,
    reloadQuota: false,
    enforceGoogleSheetsReloadEminence: false,
    referralLinks: false,
    useBuilderFunctionsProxy: true,
    useFunctionsProxy: true,
    traceImageLoadProbability: 0.05,
    traceLazyLoadingProbability: 0.05,
    cleanupFirebaseAuth: false,
    showFileQuotas: false,
    enforceFileQuotas: false,
    promoCodesGloballyAvailable: false,
    userAPIFetchColumn: false,
    allowedEmailsFromUserProfile: false,
    askForBuilderLockOverride: false,
    offlineActionQueueForEverybody: false,
    firestorePersistenceInPWA: false,
    debugLogging: false,
    cloudinaryGlideOnly: false,
    useImageKit: false,
    // Master reversible cutover: false restores legacy providers/writes. Enable after the index audit. Boolean only.
    retireCloudinary: false,
    // Enables operator-only validation wiring; server policy still authorizes every session. Boolean only.
    imageValidationClient: false,
    unauthenticatedWebhooks: false,
    cleanupAppLoginTokens: true,
    manualV2toV3Enabled: false,
    v3AnnualPlans: false,
    softEnforcementEnabled: false,
    hardEnforcementEnabled: false,
    paidSoftEnforcementEnabled: false,
    paidHardEnforcementEnabled: false,
    gettingStartedPromptEnabled: true,
    anonymousUserMiddleware: false,
    customCodeComponent: false,
    usePexelsStockPhotos: false,
    reportThisApp: false,
    createTemplatesDashboard: false,
    payAsYouGoAddon: false,
    magicLinkInPinEmail: false,
    allowAutomaticInactivityLogout: false,
    activationChecklistProbability: 0.5,
    privateMagicLinks: false,
    skipQuotaBufferEventForV4: false,
    checkImage: false,
    curateBuilderActions: false,
    fixAppVisitorCacheDamage: false,
    requestFullBodyInEnsureDataLiveliness: true,
    pingBaselineEndpoint: false,
    nudgeGmailSignUpWithGAuth: 0.5,

    // The constant this listener probability is used to reduce
    // is defined in packages/firestore-datastore/src/listener.ts
    firestorePollingListener: 1,

    enableCustomEntitlementsOverrideForAnonymousUsers: false,
    useFasterEntitlementsFetch: false,
    // Ensures plugin enrichment happens on the final version of entitlements after overlays are applied
    enrichPluginsAfterOverlay: false,

    // fallback inline computation cases from migrated classic apps with bad values
    // https://github.com/glideapps/glide/issues/33348
    fallbackInlineComputationCases: true,

    enforceBigQueryEminence: true,
    onboardingToFullScreenNewProjectFlow: 0.5,
    tutorialAppForNewOrg: false,
    reduceSnapshotMemoryConsumption: false,
    allowRedirectToCustomDomain: false,
    processDomainsDestructiveActions: false,
    showGmailForSendEmails: false,
    webhookWriteBack: false,
    proxyCloudStorage: false,
    v4billingSystem: false,
    customActionLogging: false,
    useGoogleIdentityServices: false,
    incrementalNativeTableSnapshots: false,
    enableDescopeCookieAuthentication: false,
    enableDescopeCookieAuthenticationLogging: false,
    enableBusinessFreeTrials: false,
    directDeleteMultipleRows: false,
    requestSignatureAction: false,
    chargeForQueryTables: false,
    enforcePublicUserQuota: false,
    disableReferralBonuses: false,
    useSendEmailLegacyQuota: true,
    useZapsAndWebhookLegacyQuota: true,
    countSendEmailActionAsUpdates: false,
    countZapActionAsUpdates: false,
    countWebhookActionAsUpdates: false,
    logRftEligibilityCheckDetails: false,
    glideExpertSelfServe: false,
    proxyMSGraphCalls: false,
    horizontalCollection: false,
    showActionStatusProgressBar: false,
    forceAppUserEmailWhenWritingOwnedRow: false,
    ignoreAppUserEmailInOwnedRowEnforcementInDataEditor: false,
    pubSubForJobsWhenPossible: false,
    showCalendarEvenWhenEmpty: false,
    throttleMutateTables: false,
    limitMutateTables: false,
    emailAirtableUsersAboutAuthFailure: false,
    usePlaySpecificBundle: false,
    enqueueActionsInMutateTables: false,
    enqueueActionsInEnqueueDataAction: false,
    injectDataDogRUMTrackingCodeIntoBuilder: false,
    mixpanelAnalytics: true,
    nativeTableOrderByEnqueuedAt: false,
    enableGenericAPIExperiment: false,
    debounceDataEditorEdits: false,
    alertOnFirestoreActionDataLoss: true,
    onlyListenToExistingActions: false,
    enforceSSOPlan: false,
    jdbcUseSSHuttle: false,
    newBillingLiveMode: false, // live mode being the opposite of shadow mode
    ignoreRepeatedBrokenReloads: false,
    // This is an experiment to see how much can save in costs if we do the
    // automatic refresh less frequently.
    lessFrequentAutomaticRefresh: false,
    exitProcessBuilderAppIfDeleted: false,
    // This will only set `lastSeenAt` on builder actions if the current
    // value is older than 30 minutes.
    // https://github.com/quicktype/glide/issues/21833#issuecomment-1678264346
    debounceBuilderActionLastSeenAt: false,
    // This makes it so we use native table snapshots for
    // `DataRowStore.getRowsWhere`, vs loading everything from Firestore.
    // We've been doing that for Google Sheets tables for a long time.
    useNativeTableSnapshotsForLoadingAppData: false,
    // If this flag is enabled, we only attempt to correct private-vs-public
    // row disparities within a Google Sheets reloads if we find any data
    // "out of place". This can sometimes happen when enabling row owners:
    // the mechanism for moving data between Firestore collections can time out
    // or crash, and we have to recover from this.
    // Previously we would always attempt a recovery, but even reading
    // nonexistent data incurs a cost in Firestore, and we need to reduce these
    // costs.
    deleteIncorrectRowsPathOnlyWhenNecessary: false,
    jdbcRequireIdentityEncoding: false,
    billingV4Experiment: true,
    // `billingV3SendBillingRemindersCronjobDisabled` disables the cronjob for sending billing reminders in the old
    // billing system.
    billingV3SendBillingRemindersCronjobDisabled: false,
    skipAirtableReloadAfterUpdate: false,
    deleteOldBuilderBackups: false,
    extraBackoffTimeForQueueDrops: false,
    isTrackingEnforcementEvent: false,
    billingV4ReverseFreeTrialChance: 0,
    billingV4ProcessPaymentMethodStripeWebhook: true,
    billingV4PayAsYouGoByDefault: true,
    billingV4DisableAPIv2BillingMiddleware: false,
    logPubsubMessagePublishTime: false,
    searchableColumnsInPublishedApp: false,
    // TODO: make this true by default or... better yet, actually do the work to remove the flag.
    // this is fine for testing, but ends up being used before flags are loaded and there
    // doesn't appear to be a better place to put it.
    clientVersionPollingForUpdates: false,
    clientVersionPollingForUpdatesInPlayer: false,
    enforceActionTiers: false,
    writeAppModificationMetrics: true,
    enableAppModificationMetricsCleanup: false,
    enforceComponentTiers: false,
    // When `true`, `templateSellerGetsFullRevenue` makes the seller of a template in the template store get the full
    // revenue of the sale. When `false`, the seller gets 70% of the revenue and Glide gets 30%.
    templateSellerGetsFullRevenue: false,
    disableHeartbeatEnforcement: false,
    disableHeartbeatInterval: false,
    allowNewBillingAndUsageScreen: false,
    enableCorsWhitelistAndAllowCredentials: false,
    enableCorsWhitelistAndAllowCredentialsLogging: false,
    preventAddingNewAirtableAPIKeys: false,
    hideAirtableAPIKeyForNewApps: false,
    preventDuplicateExternalSources: false,
    deprecateWebhookAction: false,
    notifyActionErrors: false,
    ensurePermissionsOnOAuthUpdate: false,
    personalUsersAPI: false,
    playerDatadogObservability: false,
    playerDatadogSampleRate: 0,
    // Removes heavy queries from the old implementation (pre billing v4) of `getQuotaStateForOrg`.
    removeHeavyQueriesFromOldQuotaStateForOrg: false,

    disableCustomPublishedFeatures: false,

    getDataSourceWarnings: false,
    // Detect external data sources whose OAuth connection was revoked/expired and notify
    // the app owner (owner email via the issue-notification pipeline + builder warning).
    // See `functions/src/data-source-connection-health.ts`.
    notifyBrokenDataSourceConnections: false,
    // NOTE: This is on by default so that tests run with it on
    elideEmptyPollingCallbacks: true,
    // NOTE: This is on by default so that tests run with it on
    airtableUseFormulaResultType: true,
    dontSaveSchemaWithDuplicateTables: false,
    dontSaveSchemaWithUnsupportedUnicodes: false,
    reportBillableRunIntegrationBIEvents: false,
    // NOTE: This is on by default so that tests run with it on
    preferRowIDInStorageController: true,
    getBuilderDeployVersionFromFirestore: false,
    keepUTCMarkerWhenParsingDateStrings: false,
    // NOTE: This is on by default so that tests run with it on
    validateGoogleDriveOAuthToken: true,
    processIncomingActionsInCronJob: false,
    allowAIComponentSuggestedFields: false,
    syncAndRetryOnPrivateRowFailure: false,
    // NOTE: This is on by default so that tests run with it on
    omitFixingForGetAllowedEdits: true,
    allowOmitScopesOnRefresh: true,
    allowAnalyzePerformanceForUsers: true,
    allowColumnAnalyzerForUsers: false,
    allowColumnAnalyzerTools: false,
    slowQueryColumnWarning: false,
    reportShortcutCRUD: false,

    setMutatingScreenKindUndefinedForOnSubmit: true,

    reportCRUDAirtableAsExternal: false,
    reportCRUDExcelAsExternal: false,
    reportCRUDBigQueryAsExternal: false,
    reportCRUDMySQLAsExternal: false,
    reportCRUDDataPluginAsExternal: false,
    reportCRUDQueryablePluginAsExternal: false,
    reportCRUDUnknownAsExternal: false,

    enforcePlayerLockoutViaDedicatedEndpoint: false,

    pubsubOtelTracing: false,
    showAIGeneratedTemplate: false,

    // Emailing
    useNotificationCenterForActionErrorEmail: false,
    useNotificationCenterForAppInviteEmail: false,
    useNotificationCenterForInAppEmail: false,
    useNotificationCenterForPinEmail: false,
    useNotificationCenterForPinEmailRendering: false,
    useNotificationCenterForTemplateSubmissionReviewEmail: false,
    useNotificationCenterForHandleZapFailureEmail: false,
    useNotificationCenterForAutomationRunFailureEmail: false,
    useNotificationCenterForBrokenAppNotificationEmail: false,
    useNotificationCenterForCourierEmails: false,
    disableEmailCTA: false,
    disableInAppEmailsCTA: false,
    proxyPinEmailIcons: false,
    useBase64EncodingForAppLoginToken: false,

    alloyDBScienceExperiments: false,

    nativeTablesAssertActionQueueCommit: false,

    aiFeaturesAndTraining: false,
    lowDeploymentVersionRefreshRate: false,

    // https://github.com/glideapps/glide/issues/30199
    enableTimeOnlyColumnComparisons: true,

    setColumnsErrorOnSkipLoading: true,
    enableRowLimitOverageToast: false,
    enableB3TracingMiddlewareForGateway: false,
    enableB3TracingMiddlewareForServices: false,

    useFloatingPointQuantities: false,

    useNewBillingUsageMetadata: false,

    makeCloudinaryCheaper: false,

    // NOTE: This is on by default so that tests run with it on
    requireDateColumnsToSendDatesInWebhooks: true,
    dontCheckForOrgsLoadedIfNoneOrg: false,
    alwaysAddFallbackUserProfileRow: false,
    allowCustomWorkflowSchedule: false,
    acceptJSONInputsFromPrimitiveColumns: false,
    // NOTE: This is on by default so that tests run with it on
    writeDateOnlyAsAgnosticToAgnosticColumns: true,
    // This will forego the "app data db" database when doing queryable
    // queries, and when doing plugins.  That means only GBT queryable data
    // sources will work, and plugins will not use cached data.
    // NOTE: Do not turn this on unless the "app data" database is down!
    doNotQueryAppDataDB: false,
    loopMultiCaseDescriptionForPopulation: false,
    // We'll toggle this once we get to the public beta
    enforceAutomationEntitlements: false,
    enforceGetAutomationSchedules: false,

    checkAppAccessWhenValidatingAction: false,
    useVisitBuilderActionWhenCopyingApps: false,

    // duplicating aps
    copyAppOmitOverwritingSourceMetadataWhenCopyingTables: false,
    copyAppCheckSourceMetadataBefore: false,
    copyAppCheckSourceMetadataAfter: false,
    copyAppCheckHasSourceMetadataAfter: false,
    // Search/replace the original app ID in the app
    // description/schema/actions with the copied app ID?
    // https://github.com/glideapps/glide/pull/31725
    copyAppRewriteAppIDs: false,
    copyAppSecrets: false,

    useNewGetOrgUsagesUpdateQuery: false,

    replaceActionsRoutesWithWorkflows: false,
    mapboxReuseMaps: false,

    useSingleIntercomUpdate: false,
    ignoreDriveItemsWithUnexpectedFormat: false,

    // See `builderNotes` in `experiments.ts`
    allowBuilderNotes: false,

    dataGridConditionalColumns: false,
    limitCustomDomainSubdomains: false,
    allowStopwatchComponent: false,
    allowDataPlotComponent: false,
    neverFailCloseModal: false,

    allowClassicAppsToPages: false,
    semiStrictConvertSerializableValue: false,
    tableStoreMigrations: false,
    enableHostCheck: false,
    enableHostRedirect: false,
    gbtUseTableStoreProbabilityForQuery: 0,
    gbtUseTableStoreProbabilityForQueryTableVersions: 0,
    gbtQueryTrafficUseTableStore: false,
    gbtQueryTableVersionsTrafficUseTableStore: false,
    gbtQueryTrafficScienceExperimentPercentage: 0,
    gbtWriteTrafficUseTableStore: false,
    gbtUseTableStoreProbabilityForWrites: 0,
    gbtPruneAccessibleTables: false,
    gbtUseTableStoreProbabilityForAPIWrites: 0,
    gbtPropagateTracingHeaders: false,
    gbtAPIQueryTrafficUseTableStore: false,
    gbtUseTableStoreProbabilityForAPIQuery: 0,
    gbtAPIQueryTrafficScienceExperimentPercentage: 0,
    gbtUseTableStoreProbabilityForSpecialTablesWrites: 0,
    gbtUseTableStoreProbabilityForBulkImportWrites: 0,
    gbtRepackBatchSize: 10,
    gbtTableDeleteBatchSize: 50,
    gbtTableDeleteTxnTimeoutMs: 120_000,
    gbtFileTrackingBatchSize: 1_000,
    gbtFileTrackingTxnTimeoutMs: 60_000,
    gbtRemainingReadsTrafficUseTableStore: false,
    gbtUseTableStoreProbabilityForRemainingReads: 0,
    gbtRemainingReadsScienceExperimentPercentage: 0,

    allowAudioEntryAction: false,
    allowStringArrayColumns: false,
    allowImprovedCalendarLocalization: false,
    // https://github.com/glideapps/glide/issues/32503
    appIsPublicFlagInAppData: false,
    inYourFaceUpgradeModalTracker: false,
    inYourFaceUpgradeModalEnabled: false,
    // If this is set to a number, that's the threshold of events above which
    // we will show the in-your-face upgrade modal.  If it's not a number, the
    // modal will not show, even if `inYourFaceUpgradeModalEnabled` is set.
    inYourFaceUpgradeModalThreshold: false,
    shareInsteadOfPublish: false,

    multipartFormDataInFunctions: false,

    frontCustomComponents: false,

    // Exposes another settings section with global app features
    // The first of which is the following flag: allowAskMyData
    exposeAppLevelGlobalFeatures: false,

    // Enables the AI powered AskMyData experiment
    allowAskMyData: false,

    allowInlineAudioTranscript: false,
    // The voice recorder sends a partial audio file to get a partial transcript
    // when it detects silence for this amount of milliseconds.
    // Set to `0` to disable.
    silenceDetectionTimeMS: 1000,
    useNativeMobileDatePicker: false,
    useDesktopDatePicker: false,
    magicLinkInFirstPublish: false,
    apiCreateBlankApp: false,
    allowTabsComponent: false,
    updateSchemaVersion: false,
    makeFallbackUserProfileRowIDBeforeUpdatingRow: false,

    fasterBuilderEminenceFlags: false,

    // Essentially a 1-for-1 copy of Call API w/ OAuth hardwired in to enable ATC API interactions
    allowATCAPI: false,

    redirectToOrgWhenNoPath: false,
    dontLoadTileAnalytics: false,
    memoizeFindTable: false,

    // This should never be turned on. See the `disableClassicApps` experiment.
    allowDisableClassicApps: false,
    useEmptyAppScreen: false,

    defaultNewUsersAiAgentFlag: false,
    allowSelfServeTrialEnd: false,
    reallyWalkAllActions: false,
    useCookiesForPlayerAuth: false,
    // NOTE: This is on by default so that tests run with it on
    noObsoleteInlineListReverse: true,
    noBuilderRowLimit: false,
    fixSmallTablesInAppFilter: false,
    dontUpdatePublishedAppDataJustVersion: false,
    invalidateDerivedValuesLess: false,
    disableEditorSwitchRedirect: false,
    memoizeGetWritableColumns: false,
    clientSideImageResizing: false,
    supportBackupsService: false,
    getTableVersionsPaginated: false,
    showBackups: false,
    catchLoadOwnerFailure: false,
    enforcePublishedAppsInPlay: false,
    enforceLockedOrgsInPlay: false,
    // Kill switch for the hard-coded redirects of Classic apps that were
    // migrated to GlideOS - see `migratedAppRedirects` in play.ts.
    redirectMigratedClassicApps: true,
    importXLSXAsBufferFirst: false,
    cacheTablesUsedInApp: false,
    queryOwnerAppIssuesBetter: false,
    ifThenElseArrayResults: false,
    enforceWriterForAppUserWriter: false,
    onlyInviteMembersAsAdmin: false,
    fixCrashForRelationTitleAndImage: false,
    showFolderPermissionsAd: false,
    preferSourceMetadataInTable: false,
    useSingleSingleSignOn: false,
    allowSingleSingleSignOn: false,
    ignoreAddArrayScreens: false,
    refuseOrgInviteOverQuota: false,
    useTemporalForBigQueryUsersExport: false,
    bigQueryFailedLogins: false,
    folderNamesInBigQueryUserExports: false,
    exportAppDataSkipSnapshot: true,
    allowPrintScreenToPDF: false,
    printScreenToPDFAdvanced: false,
    // If this is set, Location and Audio Recorder actually need a value to count as having a value for the purpose of
    // submitting forms.
    strictComponentHasValue: false,
    // This turns on the "Required" property for more components.
    requiredForMoreComponents: false,
    allowDataEditorPlus: false,
    allowDataEditorPlusForUser: false,
    allowAppDetailsInDashboard: false,

    // Intercom is migrating all customers from HMAC Identity Verification to JWT authentication.
    // When enabled, the authenticateIntercom cloud function issues a HS256 JWT instead of a
    // user_hash, and the client passes intercom_user_jwt to the Intercom messenger.
    enableIntercomJwtAuthentication: false,

    // When enabled, Intercom still boots at startup, but the first identify call
    // shuts it down and boots it again with user identity. This helps prevent
    // conversations from remaining attached to the initial anonymous boot session.
    fixLoadIntercomTiming: false,

    // Builder - Data Editor
    allowTableSearch: false,

    // Builder - Inline Computation
    lazyInlineComputationMenuOptions: false,

    // Builder - Multi-app delete
    // Global gate for multiAppDelete user feature
    allowMultiAppDelete: false,

    // Billing
    olderInvoiceDetection: false,
    // Comments Component Sync Improvement
    // When set to 0: Uses original onBlur-based sync (data loss risk on navigation)
    // When set to >0: Uses debounced sync + beforeunload fallback (number = debounce delay in milliseconds)
    // Recommended value: 1000 (1 second debounce)
    improvedCommentSync: 0,

    // Fixes the Comments component intermittently posting only the first
    // character of a comment on a fast submit. When enabled, the post action
    // reads the value committed at submit time (rather than a stale
    // hydration-time snapshot), and the submit no longer relies on a
    // `setTimeout(0)` race.
    // https://github.com/glideapps/glide/issues/38309
    fixCommentSync: false,

    // Fix for Data Grid button clicks not triggering actions (e.g., "Open link")
    // https://github.com/glideapps/glide/issues/36558
    fixDataGridButtonClick: false,

    // Fix for File Picker drag & drop only working in center of component
    // Improves drag & drop state handling to prevent flickering when moving between child elements
    // Also adds drag & drop support to single file/image picker components
    fixDragDropFilePicker: false,

    geminiGenerateImage: false,

    // Classic MCP
    // Serving gate for the hosted read-only Classic MCP server (the
    // `mcp-http-server` CLI routine): while off, the server answers 503 on
    // everything except the well-known discovery documents. Checked per
    // request; the routine polls feature settings, so flips apply within
    // ~2 minutes without a restart or redeploy.
    // https://github.com/glideapps/glide/issues/38474
    classicMcpHosted: false,

    // Serving gate for the hosted Glide Classic Support MCP (the
    // `support-mcp-http-server` CLI routine): while off, the server answers 503
    // on everything except the well-known discovery documents. Checked per
    // request; the routine polls feature settings, so flips apply within
    // ~2 minutes without a restart.
    classicSupportMcpHosted: false,
};

export type FeatureSettingValue = boolean | number | "experiment";
export type FeatureSettingName = keyof typeof defaultFeatureSettings;
export type FeatureSettings = { readonly [T in FeatureSettingName]: FeatureSettingValue };
